// Staff screen: one list of open orders, one big Prêt button per row (issue #14), a kitchen view and a pickup point
// filter (issue #18). Every string comes from @ringring/core copy; the rules for minutes, resend countdown, SMS counter,
// cancel confirmation, what each view lists and offers, and how the stored filter is resolved live there too.
// A waiting row also offers "Corriger le numéro" (issue #17): an inline line with a numeric field; the server refuses
// a duplicate with the diner's hint and anything once the order is ready, and the row disappears from edit mode then.
// The kitchen view does not offer it: correcting a number is a counter task.
// In ticket mode the form at the top is a labelled receipt field and "Ajouter un numéro de ticket" (US-34, issue #59):
// the server applies the diner's rule (1 to 6 digits, not open twice at the pickup point today) and a refusal shows
// under the field with the diner's own hint, in the screen language.
// The PIN session is a device row on the server (issue #12): every poll and every tap re-checks it, so a device
// revoked from the dashboard, signed out by a new PIN or past its 30 days is back on the PIN form within a minute,
// with a line saying why.
// A ready order whose guest registered on WhatsApp shows its WhatsApp call (issue #95): the countdown to the automatic
// call 5 minutes after Prêt, then the attempt's state, and "Appeler sur WhatsApp" to call at once. The POC demo venue
// adds "Réinitialiser la démo", which puts its seeded orders back.
import { useEffect, useRef, useState, type FormEvent, type ReactNode, type Ref } from 'react';
import {
  ALL_PICKUP_POINTS,
  LANG_DIR,
  STAFF_DEVICE,
  UI,
  addTicketCopy,
  callRowState,
  cancelConfirmCopy,
  channelCopy,
  deliveryCopy,
  editNumberCopy,
  fill,
  formatCountdown,
  minutesWaiting,
  numberErrorCopy,
  openCounts,
  parseReceiptNo,
  parseStaffView,
  resendAvailability,
  resendLabel,
  resolveStaffFilter,
  retryMinutes,
  rowActions,
  smsQuotaCopy,
  staffRows,
  type Lang,
  type StaffAction,
  type CallStatus,
  type StaffView,
  type UiCopy,
} from '@ringring/core';
import { api, errorText, subscribeBoard, type ApiError, type BoardView, type OpenTicket, type PickupPointInfo } from './api';
import { Filled, LangPicker } from './lang';
import { AuthLayout } from './AuthLayout';
import { Sidebar } from './Sidebar';
import { Icon } from './Icon';

interface VenueView {
  id: string;
  name: string;
  mode: 'scan' | 'ticket';
  default_lang: Lang;
  /** Registrations paused in the venue settings (issue #11): the QR hands out no number, the counter keeps working. */
  registrations_paused: boolean;
  sms_quota: number;
  sms_used: number;
  /** The POC demo venue (issue #95): the screen offers "Réinitialiser la démo". */
  demo?: boolean;
}

interface Session {
  venue: VenueView;
  pickup_point: { id: string; name: string };
}

/** A row's taps: the core's actions plus the WhatsApp call (issue #95). */
type Action = StaffAction | 'call';

/**
 * The venue settings (mode, pause, SMS counter) arrive at once over the board socket's `settings` message when they
 * are saved (issue #11); this poll is the safety net for a socket that missed it, and it refreshes the pickup point
 * list. The same poll re-checks the device session: a revoked device gets its 401 here at the latest, which is what
 * keeps the revocation inside `STAFF_DEVICE.revokeWithinMs`.
 */
const VENUE_POLL_MS: number = STAFF_DEVICE.checkEveryMs;
/** Minutes tick on their own; while a Renvoyer countdown runs the clock ticks every second. */
const SLOW_TICK_MS = 10_000;
const FAST_TICK_MS = 1_000;

/** Where this device remembers its view (kitchen or counter) and, per venue, the pickup point it watches. */
const VIEW_KEY = 'rr_staff_view';
const filterKey = (venueId: string) => `rr_staff_pp:${venueId}`;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage unavailable: the choice lasts for this page only
  }
}

export type PinError =
  | { code: 'wrong_pin'; attemptsLeft: number }
  | { code: 'pin_locked'; until: number }
  | { code: 'pp_not_found' }
  | { code: 'no_access' }
  | { code: 'device_revoked' }
  | { code: 'other'; error: string | undefined };

/** Text under the PIN field, in the screen language, digits as LTR runs; null once a lockout has ended. Exported for the render test. */
export function pinMessage(e: PinError, lang: Lang, now: number): ReactNode {
  const t = UI[lang];
  switch (e.code) {
    case 'wrong_pin':
      return <Filled template={t.pin_wrong} vars={{ attempts_left: String(e.attemptsLeft) }} />;
    case 'pin_locked':
      return now < e.until ? <Filled template={t.pin_locked} vars={{ minutes: String(retryMinutes(e.until - now)) }} /> : null;
    case 'pp_not_found':
      return t.pp_not_found;
    case 'no_access':
      return t.no_access;
    case 'device_revoked':
      return t.device_revoked_message;
    case 'other':
      return errorText(lang, e.error);
  }
}

/** The server said this device no longer stands: revoked from the dashboard, signed out by a new PIN, or past its 30 days. */
function isDeviceGone(code: string | undefined): boolean {
  return code === 'device_revoked' || code === 'session_expired';
}

export function Staff({ pickupPointId, lang, setLang }: { pickupPointId: string; lang: Lang; setLang: (l: Lang) => void }) {
  const t = UI[lang];
  const [session, setSession] = useState<Session | null>(null);
  const [needsPin, setNeedsPin] = useState(false);
  const [pin, setPin] = useState('');
  /** The venue's pickup points with the counts the filter shows; the sockets keep the counts live afterwards. */
  const [pickupPoints, setPickupPoints] = useState<PickupPointInfo[]>([]);
  /** One live board per pickup point of the venue. */
  const [boards, setBoards] = useState<Record<string, BoardView>>({});
  /** Sockets that reported a close and have not reconnected yet. */
  const [down, setDown] = useState<Record<string, boolean>>({});
  const [view, setViewState] = useState<StaffView>(() => parseStaffView(readStored(VIEW_KEY)));
  const [filter, setFilterState] = useState<string>(pickupPointId);
  /** The venue whose stored filter was restored, so a poll does not restore it again over a fresh choice. */
  const restoredFor = useRef<string | null>(null);
  /** The last refused action, kept as its API code and worded at render time in the screen language. */
  const [error, setError] = useState<{ code: string | undefined } | null>(null);
  const [pinError, setPinError] = useState<PinError | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [receipt, setReceipt] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'waiting' | 'ready'>('all');
  const [search, setSearch] = useState('');
  /** The server's refusal of the last receipt number typed in the form at the top (ticket mode), shown under the field. */
  const [addError, setAddError] = useState<AddError | null>(null);
  const receiptInput = useRef<HTMLInputElement>(null);
  /** The row whose ✕ was tapped and now asks "Annuler la commande n° … ?". */
  const [confirmingCancel, setConfirmingCancel] = useState<string | null>(null);
  /** The row whose ✎ was tapped and now shows the number field, with the server's refusal when there is one. */
  const [editing, setEditing] = useState<{ id: string; error: { code: string | undefined } | null } | null>(null);

  /** The venue's pickup points and their counts; restores the device's filter the first time for this venue. */
  const loadPickupPoints = async (venueId: string, fallback: PickupPointInfo) => {
    let list: PickupPointInfo[];
    try {
      list = (await api<{ pickup_points: PickupPointInfo[] }>(`/staff/${pickupPointId}/pickup-points`)).pickup_points;
    } catch {
      // Keep what we have; with nothing yet, the link's own counter still works.
      setPickupPoints((prev) => (prev.length > 0 ? prev : [fallback]));
      return;
    }
    setPickupPoints(list);
    const ids = list.map((p) => p.id);
    const first = restoredFor.current !== venueId;
    restoredFor.current = venueId;
    setFilterState((prev) => resolveStaffFilter(first ? readStored(filterKey(venueId)) : prev, ids, pickupPointId));
  };

  /** A 401 on any request sends the screen back to the PIN form; when the device was revoked or expired, the form says so. True when handled. */
  const signedOut = (err: ApiError): boolean => {
    if (err.status !== 401) return false;
    setNeedsPin(true);
    setPinError(isDeviceGone(err.code) ? { code: 'device_revoked' } : null);
    return true;
  };

  const load = async () => {
    try {
      const s = await api<Session & { board: BoardView }>(`/staff/${pickupPointId}/board`);
      setSession({ venue: s.venue, pickup_point: s.pickup_point });
      setBoards((prev) => ({ ...prev, [pickupPointId]: s.board }));
      setNeedsPin(false);
      setPinError(null);
      const counts = openCounts(s.board);
      await loadPickupPoints(s.venue.id, { ...s.pickup_point, waiting: counts.waiting, ready: counts.ready });
    } catch (e) {
      const err = e as ApiError;
      if (signedOut(err)) return;
      if (err.status === 403) {
        // Signed in as an owner or manager, but of another venue: the counter PIN still works here.
        setSession(null);
        setNeedsPin(true);
        setPinError({ code: 'no_access' });
      } else if (err.status === 404) {
        // Unknown counter, or one of another venue: this device's session cannot see it. Offer that venue's code.
        setSession(null);
        setNeedsPin(true);
        setPinError({ code: 'pp_not_found' });
      } else setError({ code: err.code });
    }
  };

  /** Venue settings, SMS counter and the pickup point list: a change in the settings reaches the screen without a reload. */
  const refreshVenue = async () => {
    try {
      const v = await api<Session>(`/staff/${pickupPointId}/venue`);
      setSession((prev) => (prev ? { ...prev, venue: v.venue, pickup_point: v.pickup_point } : prev));
      await loadPickupPoints(v.venue.id, { ...v.pickup_point, waiting: 0, ready: 0 });
    } catch (e) {
      // Revoked, signed out by a new PIN or expired: the PIN form, with the reason. Otherwise keep the last known
      // settings; the board sockets show the connection state.
      signedOut(e as ApiError);
    }
  };

  // While locked out, re-render every 10 s so the minutes left and the disabled button stay right.
  const lockedUntil = pinError?.code === 'pin_locked' ? pinError.until : null;
  useEffect(() => {
    if (lockedUntil === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(id);
  }, [lockedUntil]);
  const locked = lockedUntil !== null && now < lockedUntil;

  // A new link (another counter, or another venue): start over, and let the device's stored filter be restored again.
  useEffect(() => {
    restoredFor.current = null;
    setFilterState(pickupPointId);
    setPickupPoints([]);
    setBoards({});
    setDown({});
    void load();
  }, [pickupPointId]);

  const signedIn = session !== null && !needsPin;

  // One socket per pickup point of the venue, so the filter counts are live and switching counters is instant.
  const ppKey = pickupPoints.map((p) => p.id).join(',');
  useEffect(() => {
    if (!signedIn || ppKey === '') return;
    const ids = ppKey.split(',');
    // Every socket of the venue gets the `settings` message; one of them (the link's own counter when it is in the list) reads it.
    const settingsFrom = ids.includes(pickupPointId) ? pickupPointId : ids[0];
    const stops = ids.map((id) =>
      subscribeBoard(
        id,
        'staff',
        (b) => setBoards((prev) => ({ ...prev, [id]: b })),
        (isOnline) => {
          setDown((prev) => ({ ...prev, [id]: !isOnline }));
          // Back online: settings may have changed while the socket was down (once, on the link's own counter).
          if (isOnline && id === pickupPointId) void refreshVenue();
        },
        // The settings were saved in the dashboard: read them now, well inside the 5 s the venue settings promise.
        id === settingsFrom ? () => void refreshVenue() : undefined,
      ),
    );
    return () => stops.forEach((stop) => stop());
  }, [signedIn, ppKey]);

  useEffect(() => {
    if (!signedIn) return;
    const id = window.setInterval(() => void refreshVenue(), VENUE_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        void refreshVenue();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn, pickupPointId]);

  const rows = staffRows(boards, filter, view);
  // The clock ticks every second while a Renvoyer countdown or an automatic-call countdown is on screen.
  const counting = rows.some((o) => (o.status === 'ready' && resendAvailability(o, now).kind === 'cooldown') || callRowState(o, view, now).line?.kind === 'countdown');
  const online = !pickupPoints.some((p) => down[p.id]);

  // The clock behind "x min" and the Renvoyer countdown.
  useEffect(() => {
    if (!signedIn) return;
    const id = window.setInterval(() => setNow(Date.now()), counting ? FAST_TICK_MS : SLOW_TICK_MS);
    return () => window.clearInterval(id);
  }, [signedIn, counting]);

  const setView = (v: StaffView) => {
    setViewState(v);
    writeStored(VIEW_KEY, v);
  };

  const setFilter = (f: string) => {
    setFilterState(f);
    if (session) writeStored(filterKey(session.venue.id), f);
  };

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    setPinError(null);
    try {
      await api(`/staff/${pickupPointId}/session`, { method: 'POST', body: JSON.stringify({ pin, lang }) });
      setPin('');
      await load();
    } catch (e2) {
      const err = e2 as ApiError;
      setNow(Date.now());
      if (err.code === 'pin_locked') setPinError({ code: 'pin_locked', until: Date.now() + (err.details.retry_after ?? 60) * 1000 });
      else if (err.code === 'wrong_pin') setPinError({ code: 'wrong_pin', attemptsLeft: err.details.attempts_left ?? 0 });
      else setPinError({ code: 'other', error: err.code });
    }
  };

  /** A tap on a row goes to the row's own pickup point, whichever counters the screen shows. */
  const act = async (pp: string, id: string, action: Action) => {
    // Inside the tap, before any await: browsers only unlock audio in a user gesture.
    unlockAudio();
    setError(null);
    if (action === 'cancel') setConfirmingCancel(null);
    try {
      await api(`/staff/${pp}/tickets/${id}/${action}`, { method: 'POST' });
      confirmFeedback(action);
      setNow(Date.now());
      // Once ready or closed, the number can no longer be edited: drop a line left open on that row.
      setEditing((e) => (e?.id === id ? null : e));
    } catch (err) {
      // A revoked device learns it on its very next tap, not only at the next poll.
      if (!signedOut(err as ApiError)) setError({ code: (err as ApiError).code });
    }
  };

  /** "Enregistrer le numéro": the refusal stays on the line, in the screen language, until a new attempt. */
  const saveNumber = async (pp: string, id: string, displayNo: string) => {
    unlockAudio();
    setError(null);
    try {
      await api(`/staff/${pp}/tickets/${id}/number`, { method: 'POST', body: JSON.stringify({ display_no: displayNo }) });
      confirmFeedback('number');
      setEditing(null);
    } catch (err) {
      const e = err as ApiError;
      if (!signedOut(e)) setEditing({ id, error: { code: e.code } });
    }
  };

  // One line open at a time: the number field and the cancel question never show together.
  const askEdit = (id: string) => {
    setConfirmingCancel(null);
    setEditing({ id, error: null });
  };
  const askCancel = (id: string) => {
    setEditing(null);
    setConfirmingCancel(id);
  };

  /** New numbers go to the selected counter; with every counter shown, to the counter of the link this screen was opened with. */
  const issueAt = filter === ALL_PICKUP_POINTS ? pickupPointId : filter;

  /** Staff type the order number in both venue modes; the API checks uniqueness at the selected counter. */
  const newTicket = async (e?: FormEvent) => {
    e?.preventDefault();
    unlockAudio();
    setError(null);
    setAddError(null);
    if (parseReceiptNo(receipt) === null) {
      setAddError({ code: 'bad_number', message: '' });
      receiptInput.current?.focus();
      return;
    }
    try {
      await api(`/staff/${issueAt}/tickets`, { method: 'POST', body: JSON.stringify({ display_no: receipt }) });
      setReceipt('');
      confirmFeedback('new');
      // The next receipt can be typed at once.
      receiptInput.current?.focus();
    } catch (err) {
      const e2 = err as ApiError;
      if (signedOut(e2)) return;
      if (numberErrorCopy(lang, e2.code) !== null) {
        setAddError({ code: e2.code, message: e2.message });
        receiptInput.current?.focus();
      } else {
        setError({ code: e2.code });
      }
    }
  };

  if (needsPin)
    return <PinForm lang={lang} setLang={setLang} pin={pin} setPin={setPin} locked={locked} message={pinError ? pinMessage(pinError, lang, now) : null} onSubmit={signIn} />;

  const names = new Map(pickupPoints.map((p) => [p.id, p.name]));
  const several = pickupPoints.length > 1;
  // Live counts from the sockets; the list's own counts until a socket has delivered its first board.
  const counted = pickupPoints.map((p) => ({ id: p.id, name: p.name, open: boards[p.id] ? openCounts(boards[p.id]).open : p.waiting + p.ready }));
  const watched = filter === ALL_PICKUP_POINTS ? t.all_pickup_points : (names.get(filter) ?? session?.pickup_point.name);
  const kitchen = view === 'kitchen';
  const visibleRows = rows.filter((o) => (statusFilter === 'all' || o.status === statusFilter) && o.displayNo.includes(search.trim()));

  return (
    <StaffShell lang={lang} setLang={setLang} kitchen={kitchen} venue={session?.venue ?? null} watched={watched ?? ''}>
      {!online ? (
        <div className="banner" role="status">
          {t.offline}
        </div>
      ) : null}
      {error ? (
        <div className="banner" role="alert">
          {errorText(lang, error.code)}
        </div>
      ) : null}
      <div className="page-heading" id="service-orders">
        <div>
          <p className="eyebrow">{t.design_live_service}</p>
          <h1 className="display">{t.orders}</h1>
          <p className="muted">{t.design_staff_intro}</p>
        </div>
        <span className="live-indicator">
          <span className={`connection-dot ${online && session ? 'connected' : ''}`} />
          {session ? (online ? t.design_connected : t.offline) : t.design_connecting}
        </span>
      </div>
      <div className="service-stats">
        <div>
          <Icon name="orders" />
          <span>{t.design_open_orders}</span>
          <strong>
            <bdi dir="ltr">{rows.length}</bdi>
          </strong>
        </div>
        <div>
          <Icon name="clock" />
          <span>{t.order_preparing}</span>
          <strong>
            <bdi dir="ltr">{rows.filter((o) => o.status === 'waiting').length}</bdi>
          </strong>
        </div>
        <div className="stat-ready">
          <Icon name="check" />
          <span>{t.order_ready}</span>
          <strong>
            <bdi dir="ltr">{rows.filter((o) => o.status === 'ready').length}</bdi>
          </strong>
        </div>
      </div>
      <Tools lang={lang} view={view} setView={setView} filter={filter} setFilter={setFilter} pickupPoints={counted} />
      {!kitchen ? (
        <AddTicket
          lang={lang}
          receipt={receipt}
          setReceipt={setReceipt}
          error={addError}
          submit={newTicket}
          pickupAt={several && filter === ALL_PICKUP_POINTS ? fill(t.pickup_at, { pickup_point: names.get(issueAt) ?? issueAt }) : null}
          inputRef={receiptInput}
        />
      ) : null}
      <div className="order-toolbar">
        <div className="order-tabs" role="group" aria-label={t.orders}>
          {(['all', 'waiting', 'ready'] as const).map((state) => (
            <button type="button" key={state} aria-pressed={statusFilter === state} onClick={() => setStatusFilter(state)}>
              {state === 'all' ? t.design_all_orders : state === 'waiting' ? t.order_preparing : t.order_ready}
              <bdi dir="ltr">{state === 'all' ? rows.length : rows.filter((o) => o.status === state).length}</bdi>
            </button>
          ))}
        </div>
        <input
          className="order-search"
          type="search"
          inputMode="numeric"
          aria-label={t.design_search}
          placeholder={t.design_search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {visibleRows.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">
            <Icon name="orders" />
          </span>
          <h2>{rows.length === 0 ? t.empty : t.design_no_results}</h2>
          {rows.length === 0 ? (
            <p className="muted">{t.design_empty_hint}</p>
          ) : (
            <button
              className="btn secondary"
              type="button"
              onClick={() => {
                setStatusFilter('all');
                setSearch('');
              }}
            >
              {t.design_all_orders}
            </button>
          )}
        </div>
      ) : null}
      <div className="ticket-list">
        {visibleRows.map((o) => (
          <Row
            key={o.id}
            o={o}
            lang={lang}
            now={now}
            view={view}
            pickupPointName={several && filter === ALL_PICKUP_POINTS ? (names.get(o.pickupPointId) ?? null) : null}
            act={(id, a) => act(o.pickupPointId, id, a)}
            confirming={confirmingCancel === o.id}
            askCancel={() => askCancel(o.id)}
            keepOrder={() => setConfirmingCancel(null)}
            editing={editing?.id === o.id}
            editError={editing?.id === o.id && editing.error ? (numberErrorCopy(lang, editing.error.code) ?? errorText(lang, editing.error.code)) : null}
            askEdit={() => askEdit(o.id)}
            stopEdit={() => setEditing(null)}
            saveNumber={(no) => void saveNumber(o.pickupPointId, o.id, no)}
          />
        ))}
      </div>
      {session?.venue.demo && !kitchen ? <DemoReset lang={lang} pickupPointId={pickupPointId} onDone={() => setNow(Date.now())} onDenied={(err) => signedOut(err)} /> : null}
    </StaffShell>
  );
}

/**
 * The staff screen's frame: the screen language and direction on the container (Arabic mirrors the whole layout),
 * the logo, the venue and the counters watched, the SMS counter with its digits as LTR runs, and the language picker.
 * Exported for the render test.
 */
export function StaffShell({
  lang,
  setLang,
  kitchen,
  venue,
  watched,
  children,
}: {
  lang: Lang;
  setLang: (l: Lang) => void;
  kitchen: boolean;
  venue: (Pick<VenueView, 'name' | 'sms_used' | 'sms_quota'> & Partial<Pick<VenueView, 'id' | 'registrations_paused'>>) | null;
  watched: string;
  children?: ReactNode;
}) {
  const t = UI[lang];
  const quota = venue ? smsQuotaCopy(lang, venue.sms_used, venue.sms_quota) : null;
  return (
    <div className={kitchen ? 'app workspace staff kitchen' : 'app workspace staff'} lang={lang} dir={LANG_DIR[lang]}>
      <Sidebar lang={lang} staff venueId={venue?.id} venueName={venue?.name} />
      <div className="workspace-body">
        <header className="topbar workspace-topbar">
          <div className="venue">
            <span>
              <strong>{venue?.name}</strong> · {watched}
            </span>
            {venue && quota ? (
              <span className={`tag sms ${quota.level}`} role="status">
                <span aria-hidden="true">{quota.symbol}</span> <Filled template={t.sms_used} vars={{ used: String(venue.sms_used), quota: String(venue.sms_quota) }} />
                {quota.note ? ` · ${quota.note}` : null}
              </span>
            ) : null}
            {venue?.registrations_paused ? (
              <span className="tag call paused" role="status">
                <span aria-hidden="true">⏸</span> {t.paused_tag}
              </span>
            ) : null}
          </div>
          <LangPicker lang={lang} setLang={setLang} />
        </header>
        <main className="workspace-content">{children}</main>
      </div>
    </div>
  );
}

/**
 * The counter code form, in the screen language and direction; the code field itself stays LTR. Exported for the
 * render test; the screen shows it on 401 (no session), 403 (a session of another venue) and 404 (unknown counter).
 */
export function PinForm({
  lang,
  setLang,
  pin,
  setPin,
  locked,
  message,
  onSubmit,
}: {
  lang: Lang;
  setLang: (l: Lang) => void;
  pin: string;
  setPin: (v: string) => void;
  locked: boolean;
  message: ReactNode;
  onSubmit: (e: FormEvent) => void;
}) {
  const t = UI[lang];
  return (
    <AuthLayout lang={lang} setLang={setLang}>
      <p className="eyebrow auth-eyebrow">{t.design_team_space}</p>
      <form className="card signin pin-form" onSubmit={onSubmit}>
        <span className="form-symbol">
          <Icon name="lock" />
        </span>
        <h1>{t.design_pin_heading}</h1>
        <p className="muted">{t.design_pin_intro}</p>
        <label htmlFor="pin">{t.pin_prompt}</label>
        <input
          id="pin"
          className="code"
          maxLength={6}
          pattern="[0-9]{6}"
          required
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          dir="ltr"
          disabled={locked}
          aria-describedby={message ? 'pin-message' : undefined}
        />
        {message ? (
          <p id="pin-message" role="alert">
            {message}
          </p>
        ) : null}
        <button className="btn" type="submit" disabled={locked}>
          {t.sign_in}
        </button>
      </form>
    </AuthLayout>
  );
}

/**
 * The pickup point filter (only when the venue has several) and the kitchen view toggle. 48 px chips; the pressed
 * one is filled and carries a check, and `aria-pressed` says the same. Exported for the render test.
 */
export function Tools({
  lang,
  view,
  setView,
  filter,
  setFilter,
  pickupPoints,
}: {
  lang: Lang;
  view: StaffView;
  setView: (v: StaffView) => void;
  filter: string;
  setFilter: (f: string) => void;
  pickupPoints: { id: string; name: string; open: number }[];
}) {
  const t = UI[lang];
  const kitchen = view === 'kitchen';
  const total = pickupPoints.reduce((n, p) => n + p.open, 0);
  return (
    <div className="tools">
      {pickupPoints.length > 1 ? (
        <label className="mobile-counter-select">
          <span className="sr-only">{t.pickup_point_filter}</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value={ALL_PICKUP_POINTS}>{t.all_pickup_points}</option>
            {pickupPoints.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {pickupPoints.length > 1 ? (
        <div className="filter" role="group" aria-label={t.pickup_point_filter}>
          <Chip lang={lang} pressed={filter === ALL_PICKUP_POINTS} label={t.all_pickup_points} count={total} onClick={() => setFilter(ALL_PICKUP_POINTS)} />
          {pickupPoints.map((p) => (
            <Chip key={p.id} lang={lang} pressed={filter === p.id} label={p.name} count={p.open} onClick={() => setFilter(p.id)} />
          ))}
        </div>
      ) : null}
      <span className="spacer" />
      <button type="button" className="chip" aria-pressed={kitchen} onClick={() => setView(kitchen ? 'counter' : 'kitchen')}>
        {kitchen ? <span aria-hidden="true">✓</span> : null}
        <Icon name="kitchen" />
        {t.kitchen_view}
      </button>
    </div>
  );
}

/** A refusal of the number typed in the form at the top: the code, so the hint follows a language switch, and the API's label as a fallback. */
export interface AddError {
  code?: string;
  message: string;
}

export interface AddTicketProps {
  lang: Lang;
  receipt: string;
  setReceipt: (value: string) => void;
  /** The last refusal of the typed number, until the next attempt. */
  error: AddError | null;
  submit: (e: FormEvent) => void;
  /** "Retrait : Comptoir 1" when the screen shows every counter, so staff know where the number goes. */
  pickupAt?: string | null;
  inputRef?: Ref<HTMLInputElement>;
}

/**
 * The form at the top of the counter view: a labelled number field and "Ajouter un numéro de ticket" in every
 * venue mode. A duplicate or a malformed number is refused with the
 * diner's own hint (`numberErrorCopy`), next to the field with a symbol and words and tied to it for screen readers.
 * No `maxLength` and no `pattern`: a pasted seven-digit number must be refused with the hint, not cut to six digits or
 * stopped by the browser's own message. Exported for the render test.
 */
export function AddTicket({ lang, receipt, setReceipt, error, submit, pickupAt = null, inputRef }: AddTicketProps) {
  const copy = addTicketCopy(lang);
  const message = error ? (numberErrorCopy(lang, error.code) ?? error.message) : null;
  return (
    <form className="row add" onSubmit={submit} noValidate>
      <label htmlFor="receipt-no">{copy.field}</label>
      <input
        id="receipt-no"
        ref={inputRef}
        className="no-input"
        value={receipt}
        onChange={(e) => setReceipt(e.target.value)}
        inputMode="numeric"
        autoComplete="off"
        dir="ltr"
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? 'receipt-no-error' : undefined}
      />
      <button className="btn secondary" type="submit">
        <Icon name="plus" />
        {copy.button}
      </button>
      {pickupAt ? <span className="muted">{pickupAt}</span> : null}
      {message ? (
        <p className="error" id="receipt-no-error" role="alert">
          <span aria-hidden="true">✕</span> {message}
        </p>
      ) : null}
    </form>
  );
}

/** One filter choice: the counter's name and its open count (digits stay LTR in Arabic), named in full for screen readers. */
function Chip({ lang, pressed, label, count, onClick }: { lang: Lang; pressed: boolean; label: string; count: number; onClick: () => void }) {
  const t = UI[lang];
  return (
    <button type="button" className="chip" aria-pressed={pressed} aria-label={`${label} · ${fill(t.open_orders, { count: String(count) })}`} onClick={onClick}>
      {pressed ? <span aria-hidden="true">✓</span> : null}
      {label}
      <span className="count">
        <bdi dir="ltr">{count}</bdi>
      </span>
    </button>
  );
}

export interface RowProps {
  o: OpenTicket;
  lang: Lang;
  now: number;
  act: (id: string, a: Action) => void;
  confirming: boolean;
  askCancel: () => void;
  keepOrder: () => void;
  /** The number line is open on this row (waiting rows in the counter view only). */
  editing: boolean;
  /** The server's refusal of the last number typed, already in the screen language. */
  editError: string | null;
  askEdit: () => void;
  stopEdit: () => void;
  saveNumber: (displayNo: string) => void;
  /** Counter (default) or kitchen: what the row offers comes from `rowActions`. */
  view?: StaffView;
  /** The row's counter, shown as a tag when the screen lists every counter of the venue. */
  pickupPointName?: string | null;
}

/** One order. Exported for the render test; the screen is the only other caller. */
export function Row({
  o,
  lang,
  now,
  act,
  confirming,
  askCancel,
  keepOrder,
  editing,
  editError,
  askEdit,
  stopEdit,
  saveNumber,
  view = 'counter',
  pickupPointName = null,
}: RowProps) {
  const t = UI[lang];
  const minutes = minutesWaiting(o.createdAt, now);
  const channel = channelCopy(lang, o.channel);
  const ready = o.status === 'ready';
  // Delivery state is its own badge: the order can be "ready" while the message is still queued or has failed.
  const delivery = ready && o.delivery ? deliveryCopy(lang, o.delivery.state) : null;
  const deliveryChannel = ready && o.delivery ? channelCopy(lang, o.delivery.channel) : null;
  const resend = resendAvailability(o, now);
  // The WhatsApp call (issue #95): countdown, attempt state, or the reminder on a waiting WhatsApp row.
  const call = callRowState(o, view, now);
  const confirm = cancelConfirmCopy(lang, o.displayNo);
  const confirmId = `cancel-${o.id}`;
  // The kitchen view is Prêt only: no Retirée, no Renvoyer, no ✕ (rule in @ringring/core).
  const actions = rowActions(view, o.status);
  const can = (a: StaffAction) => actions.includes(a);
  const editId = `edit-${o.id}`;
  // The number can be corrected only while the order waits, and only at the counter: a ready row and the kitchen view have no edit control at all.
  const canEdit = !ready && view !== 'kitchen';
  const info = (
    <span className="row info">
      {view === 'counter' ? <span className="sr-only">{t.order_actions}</span> : null}
      <span className="no">
        <bdi dir="ltr">{o.displayNo}</bdi>
      </span>
      <span className="muted minutes">
        <Filled template={t.minutes_waiting} vars={{ minutes: String(minutes) }} />
      </span>
      {view === 'counter' ? <Icon name="plus" className="ticket-toggle" /> : null}
      <span className="ticket-badges">
        {pickupPointName ? <span className="tag pp">{pickupPointName}</span> : null}
        <span className={`tag ${ready ? 'ready' : ''}`}>
          <Icon name={ready ? 'check' : 'clock'} /> {ready ? t.order_ready : t.order_preparing}
        </span>
        <span className="tag">
          <Icon name={o.channel === 'whatsapp' ? 'message' : o.channel === 'none' ? 'speaker' : 'phone'} /> {channel.label}
        </span>
        {delivery && deliveryChannel ? (
          <span className={`tag delivery ${o.delivery?.state === 'failed' ? 'failed' : ''}`}>
            <Icon name={o.delivery?.state === 'failed' ? 'close' : 'message'} />
            {o.delivery?.channel !== o.channel ? <span>{deliveryChannel.label} · </span> : <span className="sr-only">{deliveryChannel.label} · </span>}
            {delivery.label}
          </span>
        ) : null}
        {o.needsCall ? (
          <span className="tag call">
            <Icon name="speaker" /> {t.needs_call}
          </span>
        ) : null}
      </span>
    </span>
  );
  const controls = (
    <>
      {call.line ? (
        <div className="row call-line" role="status">
          <span className={`tag wa-call ${call.line.kind === 'status' && call.line.status !== 'answered' && call.line.status !== 'calling' ? 'failed' : ''}`}>
            <Icon name="call" />{' '}
            {call.line.kind === 'countdown' ? (
              <Filled template={t.call_auto_in} vars={{ time: formatCountdown(call.line.secondsLeft) }} />
            ) : call.line.kind === 'after_ready' ? (
              <Filled template={t.call_after_ready} vars={{ minutes: String(call.line.minutes) }} />
            ) : (
              callStatusLabel(t, call.line.status)
            )}
          </span>
        </div>
      ) : null}
      {actions.length > 0 ? (
        <div className="row actions">
          {can('ready') ? (
            <button className="btn ready big" type="button" onClick={() => act(o.id, 'ready')}>
              <span aria-hidden="true">✓</span> {t.mark_ready}
            </button>
          ) : null}
          {can('collected') ? (
            <button className="btn big" type="button" onClick={() => act(o.id, 'collected')}>
              {t.mark_collected}
            </button>
          ) : null}
          {can('resend') ? (
            <button className="btn secondary" type="button" disabled={resend.kind !== 'available'} onClick={() => act(o.id, 'resend')}>
              {resend.kind === 'cooldown' ? <Filled template={t.resend_countdown} vars={{ seconds: String(resend.secondsLeft) }} /> : resendLabel(lang, resend)}
            </button>
          ) : null}
          {call.button !== 'hidden' ? (
            <button className="btn secondary wa-call" type="button" disabled={call.button === 'disabled'} onClick={() => act(o.id, 'call')}>
              <Icon name="call" /> {t.call_whatsapp}
            </button>
          ) : null}
          <span className="spacer" />
          {canEdit ? (
            <button className="btn secondary icon" type="button" onClick={editing ? stopEdit : askEdit} aria-label={t.edit_number} aria-expanded={editing} aria-controls={editId}>
              <Icon name="edit" />
            </button>
          ) : null}
          {can('cancel') ? (
            <button
              className="btn secondary icon"
              type="button"
              onClick={confirming ? keepOrder : askCancel}
              aria-label={t.cancel_order}
              aria-expanded={confirming}
              aria-controls={confirmId}
            >
              <Icon name="close" />
            </button>
          ) : null}
        </div>
      ) : null}
      {canEdit && editing ? <EditNumber o={o} lang={lang} id={editId} error={editError} save={saveNumber} cancel={stopEdit} /> : null}
      {confirming && can('cancel') ? (
        <div className="row confirm" id={confirmId} role="group" aria-label={confirm.question}>
          <span>
            <Filled template={t.cancel_confirm} vars={{ order_number: o.displayNo }} />
          </span>
          <button className="btn" type="button" onClick={() => act(o.id, 'cancel')}>
            {confirm.confirm}
          </button>
          <button className="btn secondary" type="button" onClick={keepOrder} autoFocus>
            {confirm.keep}
          </button>
        </div>
      ) : null}
    </>
  );

  if (view === 'kitchen') return <div className="card ticket" data-state={o.status}>{info}{controls}</div>;

  return (
    <details className="card ticket" data-state={o.status} name="staff-order-actions" onToggle={(event) => {
      // Native disclosure handles pointer/keyboard activation and keeps hidden actions out of the tab order.
      // Closing a card must also discard any pending editor or cancellation confirmation.
      if (!event.currentTarget.open) {
        if (editing) stopEdit();
        if (confirming) keepOrder();
      }
    }}>
      <summary className="ticket-summary">{info}</summary>
      <div className="ticket-controls">{controls}</div>
    </details>
  );
}

/** The attempt's state in the screen language. */
function callStatusLabel(t: UiCopy, status: Exclude<CallStatus, 'scheduled'>): string {
  switch (status) {
    case 'calling':
      return t.call_calling;
    case 'answered':
      return t.call_answered;
    case 'not_answered':
      return t.call_not_answered;
    case 'no_permission':
      return t.call_no_permission;
    case 'failed':
      return t.call_failed;
  }
}

/** Where this device remembers the presenter's WhatsApp number between demo resets. */
const DEMO_PHONE_KEY = 'rr_demo_phone';

/**
 * "Réinitialiser la démo" (issue #95), on the POC demo venue only: one tap opens a confirmation line with the
 * presenter's WhatsApp number (optional, remembered on this device); the server replaces every order with the seeded
 * ones and the boards follow over the sockets. Exported for the render test.
 */
export function DemoReset({ lang, pickupPointId, onDone, onDenied }: { lang: Lang; pickupPointId: string; onDone: () => void; onDenied: (err: ApiError) => boolean }) {
  const t = UI[lang];
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(() => readStored(DEMO_PHONE_KEY) ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await api(`/staff/${pickupPointId}/demo/reset`, { method: 'POST', body: JSON.stringify({ phone }) });
      writeStored(DEMO_PHONE_KEY, phone);
      setOpen(false);
      setMessage({ ok: true, text: t.demo_reset_done });
      onDone();
    } catch (err) {
      if (!onDenied(err as ApiError)) setMessage({ ok: false, text: errorText(lang, (err as ApiError).code) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="demo-reset">
      {message ? (
        <div className="banner" role={message.ok ? 'status' : 'alert'}>
          {message.text}
        </div>
      ) : null}
      {open ? (
        <form className="card demo-reset-form" onSubmit={(e) => void submit(e)}>
          <p>{t.demo_reset_question}</p>
          <label htmlFor="demo-phone">{t.demo_reset_phone}</label>
          <input id="demo-phone" type="tel" inputMode="tel" dir="ltr" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <div className="row">
            <button className="btn" type="submit" disabled={busy}>
              {t.demo_reset_confirm}
            </button>
            <button className="btn secondary" type="button" onClick={() => setOpen(false)}>
              {t.demo_reset_keep}
            </button>
          </div>
        </form>
      ) : (
        <button className="btn secondary" type="button" onClick={() => setOpen(true)}>
          {t.demo_reset}
        </button>
      )}
    </div>
  );
}

/**
 * The number line under a waiting row. Its own component so the field starts from the current number each time
 * the line opens; the refusal (duplicate, bad number, order already ready) is read out with the field.
 */
function EditNumber({ o, lang, id, error, save, cancel }: { o: OpenTicket; lang: Lang; id: string; error: string | null; save: (displayNo: string) => void; cancel: () => void }) {
  const copy = editNumberCopy(lang, o.displayNo);
  const [draft, setDraft] = useState(o.displayNo);
  const inputId = `${id}-no`;
  const errorId = `${id}-error`;
  return (
    <form
      className="row edit"
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        save(draft);
      }}
    >
      <label htmlFor={inputId}>
        <Filled template={UI[lang].edit_number_prompt} vars={{ order_number: o.displayNo }} />
      </label>
      <input
        id={inputId}
        className="no-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        inputMode="numeric"
        pattern="[0-9 ]*"
        maxLength={6}
        autoComplete="off"
        dir="ltr"
        autoFocus
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
      />
      <button className="btn" type="submit">
        {copy.save}
      </button>
      <button className="btn secondary" type="button" onClick={cancel}>
        {copy.cancel}
      </button>
      {error ? (
        <p className="error" id={errorId} role="alert">
          <span aria-hidden="true">✕</span> {error}
        </p>
      ) : null}
    </form>
  );
}

// ---- audible and haptic confirmation ----

let audio: AudioContext | null = null;

/** Create or resume the one AudioContext inside a tap; without this iOS keeps it suspended for good. */
function unlockAudio(): void {
  try {
    const A = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audio ??= new A();
    if (audio.state === 'suspended') void audio.resume();
  } catch {
    // no audio on this device
  }
}

/** A cashier hears and feels that the tap registered: two rising notes for Prêt, one for everything else. */
function confirmFeedback(action: Action | 'new' | 'number'): void {
  const notes = action === 'ready' ? [660, 880] : [660];
  try {
    if (navigator.vibrate) navigator.vibrate(action === 'ready' ? [50, 40, 50] : 40);
  } catch {
    // no haptics
  }
  if (!audio) return;
  try {
    notes.forEach((f, i) => {
      const o = audio!.createOscillator();
      const g = audio!.createGain();
      o.frequency.value = f;
      o.connect(g);
      g.connect(audio!.destination);
      const s = audio!.currentTime + i * 0.12;
      g.gain.setValueAtTime(0.0001, s);
      g.gain.exponentialRampToValueAtTime(0.35, s + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.22);
      o.start(s);
      o.stop(s + 0.25);
    });
  } catch {
    // no audio
  }
}
